import { useEffect, useMemo, useState } from "react";
import {
  Inbox, Sparkles, Heart, ShieldCheck, ChevronRight, Check, X,
  AlertCircle, ArrowLeft, Plus, Layers, History, Wand2,
} from "lucide-react";
import { api } from "@/lib/api";

/**
 * AdminExpertReview — Phase 2F.
 *
 * A premium triage desk for a trusted domain expert (NOT a data console).
 * Designed for 10–30 items in 10–15 minutes with almost no typing:
 *   • Home dashboard with action cards
 *   • Fast QA Review (one-card-at-a-time, large tap targets, issue chips)
 *   • AI-assisted correction draft (accept / edit / reject)
 *   • Pattern submission (lightweight form, autofill-friendly)
 *   • Review history
 *
 * Mood: warm parchment + slow gradient + serif headings + low-cognitive UX.
 */

const RATINGS = [
  { key: "excellent", label: "Excellent", emoji: "✦",  ring: "border-sage  text-sage" },
  { key: "good",      label: "Good",      emoji: "✓",  ring: "border-cocoa text-cocoa" },
  { key: "almost",    label: "Almost",    emoji: "≈",  ring: "border-mauve text-mauve" },
  { key: "off",       label: "Off",       emoji: "✕",  ring: "border-rose  text-rose" },
  { key: "unsafe",    label: "Unsafe",    emoji: "!",  ring: "border-rose  text-white bg-rose" },
];

const ISSUE_CHIPS_DEFAULT = [
  { tag: "too_generic",                       label: "Too generic" },
  { tag: "wrong_archetype",                    label: "Wrong archetype" },
  { tag: "wrong_emotional_read",               label: "Wrong emotional read" },
  { tag: "missed_nuance",                      label: "Missed nuance" },
  { tag: "missed_inconsistency",               label: "Missed inconsistency" },
  { tag: "too_harsh",                          label: "Too harsh" },
  { tag: "too_soft",                           label: "Too soft" },
  { tag: "too_clinical",                       label: "Too clinical" },
  { tag: "too_manipulative",                   label: "Too manipulative" },
  { tag: "unrealistic_advice",                 label: "Unrealistic" },
  { tag: "missed_red_flag",                    label: "Missed red flag" },
  { tag: "poor_pacing_advice",                 label: "Poor pacing" },
  { tag: "poor_reassurance_handling",          label: "Poor reassurance" },
  { tag: "poor_communication_interpretation",  label: "Poor read of comms" },
];

const ISSUE_CHIPS_UNSAFE = [
  { tag: "coercion_risk",            label: "Coercion risk" },
  { tag: "abuse_risk",               label: "Abuse risk" },
  { tag: "emotional_dependency_risk",label: "Dependency risk" },
  { tag: "self_harm_concern",        label: "Self-harm concern" },
  { tag: "legal_or_financial_risk",  label: "Legal / financial" },
  { tag: "escalation_concern",       label: "Escalation concern" },
];

function Chip({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-3 h-8 rounded-full text-[12px] border transition-all ${
        active
          ? "bg-mauve/15 border-mauve text-cocoa"
          : "bg-ivory border-champagne text-cocoa/70 hover:border-mauve/60 hover:text-cocoa"
      }`}
    >
      {children}
    </button>
  );
}

function StatTile({ label, value, accent }) {
  return (
    <div className="bg-ivory border border-champagne rounded-md p-4">
      <p className="text-[10px] tracking-[0.3em] uppercase text-cocoa/55">{label}</p>
      <p className={`font-mono text-2xl mt-2 ${accent || "text-cocoa"}`}>{value}</p>
    </div>
  );
}

function ActionCard({ icon: Icon, title, blurb, onClick, accent = "mauve", testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="group text-left bg-ivory border border-champagne rounded-md p-5 hover:border-mauve transition-all duration-200 hover:shadow-lg"
    >
      <div className="flex items-start justify-between">
        <Icon size={20} className={`text-${accent}`} />
        <ChevronRight size={14} className="text-cocoa/40 group-hover:text-cocoa transition-colors" />
      </div>
      <p className="mt-4 font-heading text-lg text-cocoa">{title}</p>
      <p className="mt-1 text-[12px] text-cocoa/65 leading-relaxed">{blurb}</p>
    </button>
  );
}

// ─── Fast QA Review ──────────────────────────────────────────────────────

function FastQAReview({ onExit }) {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [rating, setRating] = useState(null);
  const [tags, setTags] = useState([]);
  const [detail, setDetail] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState("");
  const [draftSource, setDraftSource] = useState(null);
  const [draftBusy, setDraftBusy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [doneCount, setDoneCount] = useState(0);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/admin/expert/queue?limit=30")
      .then((r) => setItems(r.data?.items || []))
      .catch((e) => setErr(e?.response?.data?.detail || "Failed to load queue"));
  }, []);

  const current = items[idx];

  useEffect(() => {
    setRating(null); setTags([]); setDetail(null); setExpanded(false); setDraft(""); setDraftSource(null);
    if (current?.id) {
      api.get(`/admin/expert/session/${current.id}`)
        .then((r) => setDetail(r.data))
        .catch(() => setDetail(null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, items.length]);

  const toggleTag = (t) => setTags((s) => s.includes(t) ? s.filter((x) => x !== t) : [...s, t]);

  const requestDraft = async () => {
    if (!current || tags.length === 0) return;
    setDraftBusy(true);
    try {
      const r = await api.post("/admin/expert/ai-suggest-correction", {
        session_id: current.id, issue_tags: tags,
      });
      setDraft(r.data?.draft || "");
      setDraftSource(r.data?.source || "template");
    } catch {
      // soft fail
    } finally {
      setDraftBusy(false);
    }
  };

  const submit = async () => {
    if (!current || !rating) return;
    setSubmitting(true);
    try {
      await api.post("/admin/expert/qa-review", {
        reviewed_object_type: "advisor_session",
        reviewed_object_id: current.id,
        rating,
        issue_tags: tags,
        suggested_correction: draft || null,
      });
      setDoneCount((d) => d + 1);
      if (idx + 1 < items.length) setIdx(idx + 1);
      else {
        // refresh queue
        const r = await api.get("/admin/expert/queue?limit=30");
        setItems(r.data?.items || []);
        setIdx(0);
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  const skip = () => {
    if (idx + 1 < items.length) setIdx(idx + 1);
  };

  const showIssueChips = rating === "almost" || rating === "off" || rating === "unsafe";
  const chipPool = rating === "unsafe" ? ISSUE_CHIPS_UNSAFE : ISSUE_CHIPS_DEFAULT;

  if (!items.length && !err) return <p className="text-cocoa/60 text-sm mt-6">Loading queue…</p>;
  if (err) return <p className="text-rose text-sm mt-6">{err}</p>;
  if (!current) {
    return (
      <div className="mt-6 ss-card-premium p-8 text-center" data-testid="qa-queue-empty">
        <Sparkles size={20} className="text-mauve mx-auto" />
        <p className="font-heading text-xl text-cocoa mt-3">Queue is clear.</p>
        <p className="mt-2 text-cocoa/65 text-sm">Reviewed {doneCount} items this session. Beautiful work.</p>
        <button onClick={onExit} className="mt-5 ss-pill ss-pill-primary h-10">Back to home</button>
      </div>
    );
  }

  return (
    <div data-testid="qa-review-mode" className="mt-2">
      <div className="flex items-center justify-between">
        <button onClick={onExit} className="text-cocoa/65 text-[12px] inline-flex items-center gap-1 hover:text-cocoa">
          <ArrowLeft size={12} /> Home
        </button>
        <p className="text-[11px] tracking-[0.3em] uppercase text-cocoa/55">
          {idx + 1} of {items.length} · {doneCount} reviewed
        </p>
      </div>

      <div className="ss-card-premium p-6 sm:p-7 mt-4" data-testid="qa-card">
        {/* Header */}
        <div className="flex flex-wrap gap-2 items-center text-[11px] uppercase tracking-wider">
          {current.archetype_name && (
            <span className="bg-mauve/10 text-mauve px-2 py-1 rounded-full">{current.archetype_name}</span>
          )}
          {current.phase && (
            <span className="bg-champagne/40 text-cocoa px-2 py-1 rounded-full">{current.phase}</span>
          )}
          {current.confidence && (
            <span className="bg-champagne/40 text-cocoa px-2 py-1 rounded-full">Conf: {current.confidence}</span>
          )}
          {current.suggested_tone && (
            <span className="bg-champagne/40 text-cocoa px-2 py-1 rounded-full">Tone: {current.suggested_tone}</span>
          )}
          {current.has_safety_note && (
            <span className="bg-rose/10 text-rose px-2 py-1 rounded-full inline-flex items-center gap-1">
              <AlertCircle size={11} /> safety
            </span>
          )}
        </div>

        {/* Situation */}
        <p className="mt-4 text-[11px] tracking-[0.3em] uppercase text-mauve">Situation (anonymised)</p>
        <p className="mt-1 text-cocoa text-[14px] leading-relaxed italic">
          “{current.situation_preview || "—"}”
        </p>

        {/* AI excerpt */}
        <p className="mt-4 text-[11px] tracking-[0.3em] uppercase text-mauve">AI's read excerpt</p>
        <p className="mt-1 text-cocoa/85 text-[14px] leading-relaxed">
          {current.ai_excerpt || "(no excerpt)"}
        </p>

        {/* Expand for full transcript */}
        <button
          onClick={() => setExpanded((v) => !v)}
          className="mt-3 text-[12px] text-cocoa/55 hover:text-cocoa underline-offset-4 hover:underline"
        >
          {expanded ? "Hide full read" : "Show full read"}
        </button>
        {expanded && detail && (
          <div className="mt-3 bg-champagne/20 border border-champagne rounded-md p-3 max-h-72 overflow-y-auto">
            <pre className="text-[11px] text-cocoa/85 whitespace-pre-wrap font-mono leading-relaxed">
              {JSON.stringify(detail.messages?.find((m) => m.role === "assistant")?.body || {}, null, 2)}
            </pre>
          </div>
        )}

        {/* Primary question */}
        <p className="mt-6 font-heading text-xl text-cocoa">How good was this read?</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {RATINGS.map((r) => {
            const active = rating === r.key;
            return (
              <button
                key={r.key}
                onClick={() => { setRating(r.key); setTags([]); setDraft(""); }}
                data-testid={`qa-rate-${r.key}`}
                className={`px-4 h-10 rounded-full text-[13px] font-medium transition-all border-2 ${
                  active ? r.ring + " bg-ivory" : "border-champagne text-cocoa/65 hover:border-cocoa/60 bg-ivory"
                }`}
              >
                <span className="mr-1.5 font-mono">{r.emoji}</span> {r.label}
              </button>
            );
          })}
        </div>

        {/* Issue chips */}
        {showIssueChips && (
          <div className="mt-5">
            <p className="text-[11px] tracking-[0.3em] uppercase text-mauve">What needs work?</p>
            <div className="mt-2 flex flex-wrap gap-2" data-testid="qa-issue-chips">
              {chipPool.map((c) => (
                <Chip
                  key={c.tag}
                  active={tags.includes(c.tag)}
                  onClick={() => toggleTag(c.tag)}
                  testId={`qa-chip-${c.tag}`}
                >
                  {c.label}
                </Chip>
              ))}
            </div>

            {/* AI-assisted correction */}
            <div className="mt-4 border-t border-champagne pt-4">
              <button
                onClick={requestDraft}
                disabled={draftBusy || tags.length === 0}
                data-testid="qa-draft-btn"
                className="ss-pill ss-pill-ghost h-9 text-[12px] inline-flex items-center gap-1 disabled:opacity-50"
              >
                <Wand2 size={12} />
                {draftBusy ? "Drafting…" : draft ? "Re-draft" : "Suggest a correction"}
              </button>
              {draft && (
                <div className="mt-3">
                  <p className="text-[11px] tracking-[0.3em] uppercase text-mauve">
                    Draft correction <span className="text-cocoa/50">· {draftSource || "—"}</span>
                  </p>
                  <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    data-testid="qa-draft-text"
                    rows={5}
                    className="mt-1 w-full bg-ivory border border-champagne rounded-md p-3 text-[13px] text-cocoa font-mono focus:border-mauve outline-none"
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Action bar */}
        <div className="mt-6 flex flex-wrap gap-3 items-center justify-between border-t border-champagne pt-5">
          <button onClick={skip} className="text-cocoa/55 text-[13px] hover:text-cocoa">Skip</button>
          <div className="flex gap-2">
            <button
              onClick={() => { setRating(null); setTags([]); setDraft(""); }}
              className="ss-pill ss-pill-ghost h-10 text-[12px]"
            >Clear</button>
            <button
              onClick={submit}
              disabled={!rating || submitting}
              data-testid="qa-submit"
              className="ss-pill ss-pill-primary h-10 disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              {submitting ? "Saving…" : (<>Save & next <ChevronRight size={14} /></>)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ─── Pattern Submission ──────────────────────────────────────────────────

function PatternSubmission({ onExit }) {
  const [form, setForm] = useState({
    pattern_name: "",
    what_it_looks_like: "",
    likely_meaning: "",
    common_signals: "",
    emotional_dynamic: "",
    support_gifting_relevance: "",
    communication_relevance: "",
    recommended_guidance: "",
    what_to_avoid: "",
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    if (form.pattern_name.length < 2 || form.what_it_looks_like.length < 4) {
      setMsg("Pattern name + what-it-looks-like are required.");
      return;
    }
    setBusy(true); setMsg("");
    try {
      const signals = form.common_signals
        .split(/[\n,]+/).map((s) => s.trim()).filter(Boolean).slice(0, 12);
      await api.post("/admin/expert/pattern-submission", {
        ...form,
        common_signals: signals,
      });
      setMsg("Pattern saved. Thank you.");
      setForm({
        pattern_name: "", what_it_looks_like: "", likely_meaning: "",
        common_signals: "", emotional_dynamic: "", support_gifting_relevance: "",
        communication_relevance: "", recommended_guidance: "", what_to_avoid: "",
      });
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Couldn't save.");
    } finally {
      setBusy(false);
    }
  };

  const TextField = ({ k, label, hint, rows = 2 }) => (
    <div>
      <p className="text-[10px] tracking-[0.3em] uppercase text-mauve">{label}</p>
      {hint && <p className="text-[11px] text-cocoa/55 mt-0.5 italic">{hint}</p>}
      <textarea
        value={form[k]}
        onChange={(e) => update(k, e.target.value)}
        rows={rows}
        data-testid={`pattern-${k}`}
        className="mt-1.5 w-full bg-ivory border border-champagne rounded-md p-3 text-[13px] text-cocoa focus:border-mauve outline-none leading-relaxed"
      />
    </div>
  );

  return (
    <div data-testid="pattern-submission" className="mt-2">
      <button onClick={onExit} className="text-cocoa/65 text-[12px] inline-flex items-center gap-1 hover:text-cocoa">
        <ArrowLeft size={12} /> Home
      </button>
      <div className="ss-card-premium p-6 sm:p-8 mt-4">
        <p className="text-[11px] tracking-[0.4em] uppercase text-mauve">New behavioural pattern</p>
        <h2 className="font-heading text-2xl text-cocoa mt-1">Add a pattern you've noticed.</h2>
        <p className="text-cocoa/65 text-sm mt-1">
          Concise is better than complete. Just sketch it; we'll iterate.
        </p>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-5">
          <TextField k="pattern_name" label="Pattern name" hint="Short, evocative" rows={1} />
          <TextField k="emotional_dynamic" label="Emotional dynamic (optional)" rows={1} />
          <div className="sm:col-span-2"><TextField k="what_it_looks_like" label="What it looks like" rows={3} /></div>
          <TextField k="likely_meaning" label="Likely meaning (optional)" rows={2} />
          <TextField k="common_signals" label="Common signals" hint="One per line or comma-separated" rows={2} />
          <TextField k="recommended_guidance" label="Recommended guidance" rows={2} />
          <TextField k="what_to_avoid" label="What to avoid" rows={2} />
          <TextField k="support_gifting_relevance" label="Gifting / support angle (optional)" rows={1} />
          <TextField k="communication_relevance" label="Communication angle (optional)" rows={1} />
        </div>

        <div className="mt-6 flex justify-between items-center border-t border-champagne pt-5">
          {msg && <p className="text-[12px] text-cocoa/70">{msg}</p>}
          <button
            onClick={submit}
            disabled={busy}
            data-testid="pattern-submit"
            className="ss-pill ss-pill-primary h-10 ml-auto disabled:opacity-50"
          >
            {busy ? "Saving…" : "Submit pattern"}
          </button>
        </div>
      </div>
    </div>
  );
}


// ─── History ─────────────────────────────────────────────────────────────

function ReviewHistory({ onExit }) {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  useEffect(() => {
    const r = filter === "all" ? "" : `&rating=${filter}`;
    api.get(`/admin/expert/history?limit=50${r}`)
      .then((res) => setItems(res.data?.items || []))
      .catch(() => setItems([]));
  }, [filter]);
  return (
    <div data-testid="review-history" className="mt-2">
      <button onClick={onExit} className="text-cocoa/65 text-[12px] inline-flex items-center gap-1 hover:text-cocoa">
        <ArrowLeft size={12} /> Home
      </button>
      <div className="ss-card-premium p-6 mt-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <p className="font-heading text-2xl text-cocoa">Review history</p>
          <div className="flex gap-1.5">
            {["all", ...RATINGS.map((r) => r.key)].map((k) => (
              <button
                key={k}
                onClick={() => setFilter(k)}
                className={`px-3 h-8 rounded-full text-[11px] uppercase tracking-wider border transition-all ${
                  filter === k ? "bg-mauve/15 border-mauve text-cocoa" : "bg-ivory border-champagne text-cocoa/60"
                }`}
              >{k}</button>
            ))}
          </div>
        </div>
        <ul className="mt-5 space-y-2">
          {items.map((r) => (
            <li key={r.id} className="bg-ivory border border-champagne rounded-md p-3 text-[13px]">
              <div className="flex justify-between gap-3">
                <span className="text-mauve font-medium capitalize">{r.rating}</span>
                <span className="text-cocoa/55 font-mono text-[11px]">{r.created_at}</span>
              </div>
              {r.issue_tags?.length > 0 && (
                <p className="mt-1 text-[12px] text-cocoa/70">{r.issue_tags.join(" · ")}</p>
              )}
              {r.suggested_correction && (
                <p className="mt-1 text-cocoa/85 text-[12px] italic">"{r.suggested_correction.slice(0, 200)}"</p>
              )}
            </li>
          ))}
          {items.length === 0 && <p className="text-cocoa/55 text-sm">No reviews yet.</p>}
        </ul>
      </div>
    </div>
  );
}


// ─── Top-level shell ─────────────────────────────────────────────────────

export function AdminExpertReview() {
  const [stats, setStats] = useState(null);
  const [view, setView] = useState("home");

  useEffect(() => {
    api.get("/admin/expert/stats").then((r) => setStats(r.data)).catch(() => setStats(null));
  }, [view]);

  const top = useMemo(() => stats?.top_issue_tags?.slice(0, 5) || [], [stats]);

  if (view === "qa") return <FastQAReview onExit={() => setView("home")} />;
  if (view === "pattern") return <PatternSubmission onExit={() => setView("home")} />;
  if (view === "history") return <ReviewHistory onExit={() => setView("home")} />;

  return (
    <div data-testid="expert-review" className="space-y-6">
      <div>
        <p className="text-[11px] tracking-[0.4em] uppercase text-mauve">Expert review</p>
        <h2 className="font-heading text-3xl text-cocoa mt-1">Triage desk.</h2>
        <p className="text-cocoa/65 text-sm mt-1">
          Review AI outputs at speed. 10–30 items in 10–15 minutes. Each correction becomes structured platform IP.
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatTile label="Pending QA" value={stats?.pending_qa ?? "—"} accent="text-mauve" />
        <StatTile label="Reviews total" value={stats?.reviews_total ?? "—"} />
        <StatTile label="Patterns pending" value={stats?.patterns_pending ?? "—"} />
        <StatTile label="Unsafe flagged" value={stats?.reviews_by_rating?.unsafe ?? 0} accent="text-rose" />
      </div>

      {top.length > 0 && (
        <div className="bg-ivory border border-champagne rounded-md p-4">
          <p className="text-[10px] tracking-[0.3em] uppercase text-cocoa/55">Top issue tags this period</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {top.map((t) => (
              <span key={t.tag} className="bg-mauve/10 text-mauve px-3 h-7 rounded-full text-[12px] inline-flex items-center">
                {t.tag.replaceAll("_"," ")} · {t.n}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <ActionCard
          icon={Inbox}
          title="Fast QA Review"
          blurb="One card at a time. Rate, tag, save."
          onClick={() => setView("qa")}
          testId="action-qa"
        />
        <ActionCard
          icon={Plus}
          title="New pattern"
          blurb="Sketch a behavioural pattern you've noticed."
          onClick={() => setView("pattern")}
          accent="sage"
          testId="action-pattern"
        />
        <ActionCard
          icon={History}
          title="Review history"
          blurb="Your previous corrections + audit trail."
          onClick={() => setView("history")}
          accent="cocoa"
          testId="action-history"
        />
        <ActionCard
          icon={Heart}
          title="Tone review"
          blurb="Same QA flow filtered to tone-mismatch tags."
          onClick={() => setView("qa")}
          accent="mauve"
          testId="action-tone"
        />
        <ActionCard
          icon={Layers}
          title="Archetype review"
          blurb="Same QA flow filtered to archetype mismatches."
          onClick={() => setView("qa")}
          accent="cocoa"
          testId="action-archetype"
        />
        <ActionCard
          icon={ShieldCheck}
          title="Safety review"
          blurb="High-priority cards with safety notes."
          onClick={() => setView("qa")}
          accent="rose"
          testId="action-safety"
        />
      </div>
    </div>
  );
}

export default AdminExpertReview;
